import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import API_BASE from "./config";
import "./css/admin.css";
import { usePermissions, RequirePermission } from "./hooks/usePermissions";
import S3Integration from "./S3Integration";

// Friendly labels for user_credit_ledger.type values (charges only).
const CHARGE_TYPE_LABELS = {
  workflow_charge: "Workflow run",
  workflow_creation_charge: "Workflow creation",
  egress_charge: "Data egress",
  notebook_charge: "Notebook session",
  interpretation_charge: "Interpretation",
  data_download_charge: "Data download",
  storage_charge: "S3 storage",
  adjustment: "Adjustment",
};

const OrgAdminPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [organization, setOrganization] = useState(null);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [editUser, setEditUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [pendingRequests, setPendingRequests] = useState([]);
  const [accessRequests, setAccessRequests] = useState([]);
  const [inviteMessage, setInviteMessage] = useState("");
  const normalizedStatus = (status) =>
  typeof status === "string"
    ? status.toLowerCase()
    : status === false
    ? "disabled"
    : "enabled";

  const activeUsers = users.filter(u => normalizedStatus(u.status) === "enabled");
  const inactiveUsers = users.filter(u => normalizedStatus(u.status) === "disabled");
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [resourceForm, setResourceForm] = useState({
    max_vcpu: "",
    max_memory: "",
    max_concurrent_users: ""
  });
  const [showConfirm, setShowConfirm] = useState(false);
  const [orgCredits, setOrgCredits] = useState(null);
  const [loadingCredits, setLoadingCredits] = useState(false);
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [buyAmount, setBuyAmount] = useState(100);
  const [creditHistory, setCreditHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10; // fixed page size
  const [sortColumn, setSortColumn] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc"); // "asc" or "desc"
    // Filters
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterMinAmount, setFilterMinAmount] = useState("");
  const [filterMaxAmount, setFilterMaxAmount] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showAllocateModal, setShowAllocateModal] = useState(false);
  const [activeSection, setActiveSection] = useState("org-details"); // left-nav selected section
  const [userAllocations, setUserAllocations] = useState({});
  const [userBalances, setUserBalances] = useState({});
  const [loadingUserBalances, setLoadingUserBalances] = useState(false);
  const [allocationError, setAllocationError] = useState("");
  const [creditRequests, setCreditRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);

  // Monthly statements + top-up receipts (downloadable branded PDFs).
  const [billingInvoices, setBillingInvoices] = useState([]);

  // Report branding (company-admin managed): logo + company name shown on
  // interpretation report headers. Stored per organization.
  const [reportBranding, setReportBranding] = useState({ company_name: "", logo_data_uri: "" });
  const [savingBranding, setSavingBranding] = useState(false);
  const [brandingMessage, setBrandingMessage] = useState("");

  // Company-wide charges table (filterable by date range + user)
  const [charges, setCharges] = useState([]);
  const [chargeUsers, setChargeUsers] = useState([]);
  const [chargeTotals, setChargeTotals] = useState({ total: 0, byType: {} });
  const [loadingCharges, setLoadingCharges] = useState(false);
  const [chargeStartDate, setChargeStartDate] = useState("");
  const [chargeEndDate, setChargeEndDate] = useState("");
  const [chargeUserFilter, setChargeUserFilter] = useState("all");


  // Logged-in admin's groups (from first user entry)
  const loggedInAdminGroups = users.find(u => u.email === organization?.admin_email)?.groups || [];
  const isSuperAdmin = loggedInAdminGroups.includes("cloudgenomics-admin");

  useEffect(() => {
    fetchOrganization();
    fetchUsers();
    fetchGroups();
    fetchPendingRequests();
    fetchAccessRequests();
    loadOrgCredits();
    loadBillingInvoices();
    fetchCreditRequests();
    fetchReportBranding();
    loadCharges();
  }, []);

  useEffect(() => {
    if (users.length > 0 && availableGroups.length > 0) {
      loadUserBalances();
    }
  }, [users, availableGroups]);

  function getSortedHistory(data = creditHistory) {
    const sorted = [...data];
    sorted.sort((a, b) => {
      let valA = a[sortColumn];
      let valB = b[sortColumn];

      // Convert amounts to numbers
      if (sortColumn === "amount") {
        valA = Number(valA);
        valB = Number(valB);
      }

      // Convert dates to timestamps
      if (sortColumn === "created_at") {
        valA = new Date(valA).getTime();
        valB = new Date(valB).getTime();
      }

      if (valA < valB) return sortDirection === "asc" ? -1 : 1;
      if (valA > valB) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }

  function handleSort(column) {
    if (sortColumn === column) {
      // Toggle direction
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // New column
      setSortColumn(column);
      setSortDirection("asc");
    }

    // Reset to page 1 when sorting changes
    setCurrentPage(1);
  }

  function downloadCSV() {
    const filteredData = getSortedHistory(getFilteredHistory());

    if (!filteredData || filteredData.length === 0) {
      alert("No filtered results to export.");
      return;
    }

    const header = ["Date", "Type", "Amount", "Description"];

    const rows = filteredData.map(entry => [
      new Date(entry.created_at).toLocaleString(),
      entry.type,
      Number(entry.amount),
      entry.description.replace(/"/g, '""')
    ]);

    const csvContent =
      header.join(",") +
      "\n" +
      rows.map(r => r.map(v => `"${v}"`).join(",")).join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "credit_purchase_history.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  function getFilteredHistory() {
    return creditHistory.filter(entry => {
      const entryDate = new Date(entry.created_at);
      const entryAmount = Number(entry.amount);

      // Start date filter
      if (filterStartDate) {
        const start = new Date(filterStartDate);
        if (entryDate < start) return false;
      }

      // End date filter
      if (filterEndDate) {
        const end = new Date(filterEndDate);
        if (entryDate > end) return false;
      }

      // Type filter
      if (filterType !== "all" && entry.type !== filterType) {
        return false;
      }

      // Min amount
      if (filterMinAmount && entryAmount < Number(filterMinAmount)) {
        return false;
      }

      // Max amount
      if (filterMaxAmount && entryAmount > Number(filterMaxAmount)) {
        return false;
      }

      // Free-text search
      if (searchQuery.trim() !== "") {
        const q = searchQuery.toLowerCase();

        const matches =
          entry.type.toLowerCase().includes(q) ||
          entry.description.toLowerCase().includes(q) ||
          String(entryAmount).includes(q) ||
          new Date(entry.created_at).toLocaleString().toLowerCase().includes(q);

        if (!matches) return false;
      }

      return true;
    });
  }

  const filtered = getFilteredHistory();
  const sorted = getSortedHistory(filtered);
  
  function handleAllocateCredits() {
    console.log("Allocate Credits clicked");
  }


  const authHeader = () => ({
    Authorization: `Bearer ${localStorage.getItem("idToken")}`
  });

  const fetchOrganization = async () => {
    try {
      const res = await axios.get(`${API_BASE}/org-admin/organization`, {
        headers: authHeader()
      });
      setOrganization(res.data);
    } catch (err) {
      console.error("Error fetching organization:", err);
      setError("Failed to fetch organization details.");
    }
  };

  const fetchReportBranding = async () => {
    try {
      const res = await axios.get(`${API_BASE}/org-admin/report-branding`, {
        headers: authHeader()
      });
      setReportBranding({
        company_name: res.data.company_name || "",
        logo_data_uri: res.data.logo_data_uri || ""
      });
    } catch (err) {
      console.error("Error fetching report branding:", err);
    }
  };

  const handleLogoFile = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!/^image\/(png|jpeg|jpg|svg\+xml|webp|gif)$/i.test(file.type)) {
      setBrandingMessage("Logo must be a PNG, JPEG, SVG, WEBP or GIF image.");
      return;
    }
    if (file.size > 1_000_000) {
      setBrandingMessage("Logo is too large (max ~1MB). Please use a smaller image.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setReportBranding(prev => ({ ...prev, logo_data_uri: String(reader.result) }));
      setBrandingMessage("");
    };
    reader.readAsDataURL(file);
  };

  const saveReportBranding = async () => {
    // Both are required — interpretation is blocked until branding is complete.
    if (!reportBranding.company_name || !reportBranding.company_name.trim()) {
      setBrandingMessage("Company name is required.");
      return;
    }
    if (!reportBranding.logo_data_uri) {
      setBrandingMessage("A company logo is required.");
      return;
    }
    setSavingBranding(true);
    setBrandingMessage("");
    try {
      await axios.post(
        `${API_BASE}/org-admin/report-branding`,
        {
          company_name: reportBranding.company_name,
          logo_data_uri: reportBranding.logo_data_uri
        },
        { headers: authHeader() }
      );
      setBrandingMessage("Report branding saved.");
    } catch (err) {
      console.error("Error saving report branding:", err);
      setBrandingMessage(err.response?.data?.error || "Failed to save report branding.");
    } finally {
      setSavingBranding(false);
    }
  };

  const openResourceModal = () => {
    if (!organization) return;

    setResourceForm({
      max_vcpu: organization.max_vcpu,
      max_memory: organization.max_memory,
      max_concurrent_users: organization.max_concurrent_users
    });

    setShowResourceModal(true);
  };

  const submitResourceChange = async () => {
    try {
      await axios.post(
        `${API_BASE}/org-admin/request-resource-change`,
        {
          max_vcpu: resourceForm.max_vcpu,
          max_memory: resourceForm.max_memory,
          max_concurrent_users: resourceForm.max_concurrent_users
        },
        { headers: authHeader() }
      );

      alert("Your request has been submitted to CloudGenomics Support.");
      setShowConfirm(false);
      setShowResourceModal(false);
    } catch (err) {
      console.error("Error submitting resource change:", err);
      alert("Failed to submit request. Please try again.");
    }
  };

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const res = await axios.get(`${API_BASE}/org-admin/users`, {
        headers: authHeader()
      });
      console.log("Fetched users:", res.data);
      setUsers(res.data);
      setError("");
    } catch (err) {
      console.error("Error fetching users:", err);
      setError("Failed to fetch users.");
    } finally {
      setIsLoading(false);
    }
  };

  async function fetchCreditRequests() {
    try {
      setLoadingRequests(true);

      const res = await axios.get(
        `${API_BASE}/org-admin/credit-requests`,
        { headers: authHeader() }
      );

      setCreditRequests(res.data.requests || []);
    } catch (err) {
      console.error("Error loading credit requests:", err);
      setCreditRequests([]);
    } finally {
      setLoadingRequests(false);
    }
  }

  async function handleApproveRequest(req) {
    try {
      const res = await axios.post(
        `${API_BASE}/org-admin/credit-requests/approve`,
        { request_id: req.id },
        { headers: authHeader() }
      );

      if (res.data.success) {
        alert("Request approved.");
        fetchCreditRequests();
        loadUserBalances();
      }
    } catch (err) {
      console.error("Error approving request:", err);
      alert("Failed to approve request.");
    }
  }

  async function handleRejectRequest(req) {
    const reason = prompt("Enter rejection reason (optional):");

    try {
      const res = await axios.post(
        `${API_BASE}/org-admin/credit-requests/reject`,
        { request_id: req.id, reason },
        { headers: authHeader() }
      );

      if (res.data.success) {
        alert("Request rejected.");
        fetchCreditRequests();
      }
    } catch (err) {
      console.error("Error rejecting request:", err);
      alert("Failed to reject request.");
    }
  }

  const fetchGroups = async () => {
    try {
      const res = await axios.get(`${API_BASE}/org-admin/available-groups`, {
        headers: authHeader()
      });

      // Filter out restricted groups unless super-admin
      const restricted = ["cloudgenomics", "cloudgenomics-admin"];
      const filtered = isSuperAdmin
        ? res.data
        : res.data.filter(g => !restricted.includes(g));

      setAvailableGroups(filtered);
    } catch (err) {
      console.error("Error fetching groups:", err);
      setError("Failed to fetch available groups.");
    }
  };

  const fetchPendingRequests = async () => {
    try {
      const res = await axios.get(`${API_BASE}/org-admin/pending-requests`, {
        headers: authHeader()
      });
      setPendingRequests(res.data);
    } catch (err) {
      console.error("Error fetching pending requests:", err);
    }
  };

  // Self-service access requests (from the public /request-access form) addressed to me.
  const fetchAccessRequests = async () => {
    try {
      const res = await axios.get(`${API_BASE}/org-admin/access-requests`, { headers: authHeader() });
      setAccessRequests(res.data);
    } catch (err) {
      console.error("Error fetching access requests:", err);
    }
  };

  const approveAccessRequest = async (id) => {
    try {
      const res = await axios.post(`${API_BASE}/org-admin/access-requests/${id}/approve`, {}, { headers: authHeader() });
      alert(res.data.message || "Approved — a registration invite was sent.");
      fetchAccessRequests();
      fetchPendingRequests();
    } catch (err) {
      alert(err.response?.data?.error || "Failed to approve request.");
    }
  };

  const rejectAccessRequest = async (id) => {
    if (!window.confirm("Reject this access request?")) return;
    try {
      await axios.post(`${API_BASE}/org-admin/access-requests/${id}/reject`, {}, { headers: authHeader() });
      fetchAccessRequests();
    } catch (err) {
      alert(err.response?.data?.error || "Failed to reject request.");
    }
  };

  const handleGroupChange = (userEmail, groupName, isChecked) => {
    setUsers(users.map(user => {
      if (user.email === userEmail) {
        const updatedGroups = isChecked
          ? [...user.groups, groupName]
          : user.groups.filter(g => g !== groupName);
        return { ...user, groups: updatedGroups };
      }
      return user;
    }));
  };

  const saveUserChanges = async (user) => {
    try {
      await axios.post(
        `${API_BASE}/org-admin/assign-group`,
        { username: user.email, groups: user.groups },
        { headers: authHeader() }
      );
      alert(`User ${user.name} updated successfully.`);
      setEditUser(null);
      fetchUsers();
    } catch (err) {
      console.error("Error updating user:", err);
      alert("Failed to update user. Please try again.");
    }
  };

  const toggleUserStatus = async (email, isDisabled) => {
    try {
      const endpoint = isDisabled ? "enable-user" : "disable-user";
      const res = await axios.post(
        `${API_BASE}/org-admin/${endpoint}`,
        { username: email },
        { headers: authHeader() }
      );
      alert(res.data.message);
      fetchUsers();
    } catch (err) {
      console.error("Error toggling user status:", err);
      alert("Failed to toggle user status. Please try again.");
    }
  };

  const removeUser = async (email) => {
    if (!window.confirm(`Permanently remove ${email} from your organization?\n\nTheir login (Cognito account) will be deleted and they will be removed from your organization. Their data is retained under the organization. This cannot be undone.`)) return;
    try {
      const res = await axios.post(
        `${API_BASE}/org-admin/remove-user`,
        { username: email },
        { headers: authHeader() }
      );
      alert(res.data.message);
      fetchUsers();
    } catch (err) {
      console.error("Error removing user:", err);
      alert(err.response?.data?.error || "Failed to remove user.");
    }
  };

  const inviteUser = async () => {
    if (!inviteEmail.trim()) return alert("Enter an email address.");
    try {
      const res = await axios.post(
        `${API_BASE}/org-admin/invite-user`,
        { email: inviteEmail },
        { headers: authHeader() }
      );
      setInviteMessage(`Invitation sent to ${inviteEmail}.`);
      setInviteEmail("");
      fetchPendingRequests();
    } catch (err) {
      console.error("Error inviting user:", err);
      alert("Failed to send invitation.");
    }
  };

  const resendInvite = async (id) => {
    try {
      await axios.post(
        `${API_BASE}/org-admin/resend-invite`,
        { id },
        { headers: authHeader() }
      );
      alert("Invitation resent.");
    } catch (err) {
      console.error("Error resending invite:", err);
      alert("Failed to resend invitation.");
    }
  };

  const deleteInvite = async (id) => {
    try {
      await axios.post(
        `${API_BASE}/org-admin/delete-invite`,
        { id },
        { headers: authHeader() }
      );
      fetchPendingRequests();
    } catch (err) {
      console.error("Error deleting invite:", err);
      alert("Failed to remove invitation.");
    }
  };

  const approveRequest = async (email) => {
    try {
      await axios.post(
        `${API_BASE}/org-admin/add-user-to-org`,
        { email },
        { headers: authHeader() }
      );
      fetchPendingRequests();
      fetchUsers();
    } catch (err) {
      console.error("Error approving request:", err);
      alert("Failed to add user to org.");
    }
  };

  async function loadOrgCredits() {
    setLoadingCredits(true);
    setOrgCredits(null);

    try {
      const res = await axios.get(
        `${API_BASE}/org-admin/credits`,
        { headers: authHeader() }
      );

      const balance =
        typeof res.data.balance === "number" ? res.data.balance : 0;

      setOrgCredits(balance);
      setLoadingCredits(false);
    } catch (err) {
      console.error("Error loading org credits:", err);
      setOrgCredits(null);
      setLoadingCredits(false);
    }
  }

  async function loadBillingInvoices() {
    try {
      const res = await axios.get(`${API_BASE}/org-admin/billing/summary`, { headers: authHeader() });
      setBillingInvoices(res.data.invoices || []);
    } catch (err) {
      console.error("Error loading billing statements:", err);
      setBillingInvoices([]);
    }
  }

  async function downloadBillingInvoice(id) {
    try {
      const res = await axios.get(`${API_BASE}/org-admin/billing/invoices/${id}/pdf`, { headers: authHeader() });
      if (res.data.url) window.open(res.data.url, "_blank", "noopener");
    } catch (err) {
      console.error("Error downloading invoice:", err);
      alert("Could not download that document.");
    }
  }

  async function handleBuyCredits() {
    try {
      const numericAmount = Number(buyAmount);

      if (isNaN(numericAmount) || numericAmount < 100) {
        alert("Minimum purchase is $100.");
        return;
      }

      const res = await axios.post(
        `${API_BASE}/org-admin/credits/create-checkout-session`,
        { amount: numericAmount },
        { headers: authHeader() }
      );

      if (res.data.url) {
        window.location.href = res.data.url;
      } else {
        alert("Failed to start Stripe checkout session.");
      }
    } catch (err) {
      console.error("Error creating Stripe checkout session:", err);
      alert("Failed to start checkout session.");
    }
  }

  async function handleAllocateCredits() {
    setShowAllocateModal(true);
    setCurrentPage(1); // not required but consistent UX
    await loadUserBalances();
  }

  async function handleSubmitAllocation() {
    // Final guard
    if (!validateAllocations()) return;

    // Build payload: only positive allocations
    const allocations = Object.entries(userAllocations)
      .filter(([, amount]) => Number(amount) > 0)
      .map(([userSub, amount]) => ({
          user_sub: userSub,
          amount: Number(amount)
      }));


    if (allocations.length === 0) {
      alert("No positive allocations to submit.");
      return;
    }

    try {
      const res = await axios.post(
        `${API_BASE}/org-admin/credits/allocate`,
        { allocations },
        { headers: authHeader() }
      );

      if (res.data && res.data.success) {
          // Update org balance
          if (typeof res.data.organization_balance === "number") {
            setOrgCredits(res.data.organization_balance);
          } else {
            loadOrgCredits();
          }

          // Refresh user balances
          await loadUserBalances();

          // Reset allocations
          setUserAllocations({});

          // Refresh history (optional)
          loadCreditHistory();

          alert(`Successfully allocated ${res.data.total_allocated} credits.`);
          setShowAllocateModal(false);
        }
      else {
        alert("Allocation failed. Please try again.");
      }
    } catch (err) {
      console.error("Error allocating credits:", err);
      alert(
        err.response?.data?.error ||
        "Failed to allocate credits. Please try again."
      );
    }
  }

  async function loadCreditHistory() {
    try {
      setLoadingHistory(true);

      const res = await axios.get(
        `${API_BASE}/org-admin/credits/history`,
        { headers: authHeader() }
      );

      setCreditHistory(res.data.history || []);
    } catch (err) {
      console.error("Error loading credit history:", err);
      setCreditHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  }

  async function loadCharges() {
    try {
      setLoadingCharges(true);
      const params = {};
      if (chargeStartDate) params.startDate = chargeStartDate;
      if (chargeEndDate) params.endDate = chargeEndDate;
      if (chargeUserFilter && chargeUserFilter !== "all") params.userSub = chargeUserFilter;

      const res = await axios.get(
        `${API_BASE}/org-admin/charges`,
        { headers: authHeader(), params }
      );

      setCharges(res.data.charges || []);
      setChargeUsers(res.data.users || []);
      setChargeTotals(res.data.totals || { total: 0, byType: {} });
    } catch (err) {
      console.error("Error loading company charges:", err);
      setCharges([]);
      setChargeTotals({ total: 0, byType: {} });
    } finally {
      setLoadingCharges(false);
    }
  }

  function downloadChargesCSV() {
    if (!charges || charges.length === 0) { alert("No charges to export."); return; }
    const header = ["Date", "User", "Email", "Type", "Amount", "Description", "Execution"];
    const rows = charges.map(c => [
      new Date(c.created_at).toLocaleString(),
      c.name || "",
      c.email || c.user_sub,
      CHARGE_TYPE_LABELS[c.type] || c.type,
      Math.abs(Number(c.amount)).toFixed(2),
      (c.description || "").replace(/"/g, '""'),
      c.related_workflow_execution_id || "",
    ]);
    const csv = [header, ...rows]
      .map(r => r.map(v => `"${String(v)}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `company-charges-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function loadUserBalances() {
    try {
      setLoadingUserBalances(true);

      const res = await axios.get(
        `${API_BASE}/org-admin/credits/user-balances`,
        { headers: authHeader() }
      );

      const map = {};
      res.data.balances.forEach(u => {
        map[u.user_sub] = u.balance;
      });

      setUserBalances(map);

      // Initialize allocation inputs to 0
      const allocInit = {};
      res.data.balances.forEach(u => {
        allocInit[u.user_sub] = 0;
      });
      setUserAllocations(allocInit);


    } catch (err) {
      console.error("Error loading user balances:", err);
    } finally {
      setLoadingUserBalances(false);
    }
  }

  function validateAllocations() {
    let total = 0;

    for (const [userId, amount] of Object.entries(userAllocations)) {
      const num = Number(amount);

      // Must be a number
      if (isNaN(num)) {
        setAllocationError("All allocations must be numbers.");
        return false;
      }

      // Must be integer
      if (!Number.isInteger(num)) {
        setAllocationError("All allocations must be whole numbers.");
        return false;
      }

      // Must be >= 0
      if (num < 0) {
        setAllocationError("Allocations cannot be negative.");
        return false;
      }

      total += num;
    }

    // Must not exceed org balance
    if (total > orgCredits) {
      setAllocationError(
        `Total allocation (${total}) exceeds available organization credits (${orgCredits}).`
      );
      return false;
    }

    // All good
    setAllocationError("");
    return true;
  }

  if (isLoading) return <div className="loading">Loading…</div>;

  return (
    <div className="admin-container">
      <h1>Admin Dashboard</h1>
      <RequirePermission permission="manageAdmin" showMessage>

      <div style={{ display: "flex", gap: 20, alignItems: "flex-start", marginTop: 12 }}>
        <nav style={{ width: 232, flexShrink: 0, position: "sticky", top: 16, display: "flex", flexDirection: "column", gap: 4 }}>
          {[
            ["org-details", "Organization Details"],
            ["s3", "S3 Integration"],
            ["report", "Report"],
            ["credits", "Organization Credits"],
            ["statements", "Statements & Receipts"],
            ["charges", "Company Charges"],
            ["invite", "Invite User"],
            ["pending", "Pending Access Requests"],
            ["access", "Access Requests"],
            ["credit-requests", "User Credit Requests"],
            ["active-users", "Active Users"],
            ["inactive-users", "Inactive Users"],
          ].map(([id, label]) => (
            <button
              key={id}
              onClick={() => setActiveSection(id)}
              style={{
                display: "block", width: "100%", textAlign: "left", padding: "10px 12px",
                border: "none", borderRadius: 8, cursor: "pointer", fontSize: 14,
                background: activeSection === id ? "#2563eb" : "transparent",
                color: activeSection === id ? "#fff" : "#1e293b",
                fontWeight: activeSection === id ? 600 : 400,
              }}
            >
              {label}
            </button>
          ))}
        </nav>

        <div style={{ flex: 1, minWidth: 0 }}>

      {/* ORG DETAILS */}
      {organization && (
      <div className="org-card" style={{ display: activeSection === "org-details" ? "block" : "none" }}>
        <h2>Organization Details</h2>

        <table className="admin-table">
          <tbody>
            <tr><th>Name</th><td>{organization.organization_name}</td></tr>
            <tr><th>Status</th><td>{organization.status}</td></tr>
            <tr><th>Billing Active</th><td>{organization.billing_active ? "Yes" : "No"}</td></tr>
            <tr><th>Max vCPU</th><td>{organization.max_vcpu}</td></tr>
            <tr><th>Max Memory (GB)</th><td>{organization.max_memory}</td></tr>
            <tr><th>Billing Contact</th><td>{organization.billing_contact_email}</td></tr>
          </tbody>
        </table>

        {/* ⭐ Restored button */}
        <button
          className="btn btn-edit"
          style={{ marginTop: "16px" }}
          onClick={openResourceModal}
        >
          Request Resource Change
        </button>
      </div>
      )}

      <div className="org-card" style={{ display: activeSection === "s3" ? "block" : "none" }}>
        <S3Integration />
      </div>

      <div className="org-card" style={{ display: activeSection === "report" ? "block" : "none" }}>
        <h2>Report</h2>
        <p style={{ color: "#64748b", marginTop: 0 }}>
          Branding for your interpretation reports. Your logo appears on the left of every
          page header and your company name on the report. The footer always shows
          &ldquo;Generated with CloudGenomics&rdquo;.
        </p>

        <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 320px", minWidth: 280 }}>
            <label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Company name</label>
            <input
              type="text"
              value={reportBranding.company_name}
              onChange={(e) => setReportBranding(prev => ({ ...prev, company_name: e.target.value }))}
              placeholder="e.g. Acme Biosciences"
              maxLength={255}
              style={{ width: "100%", padding: "8px 10px", border: "1px solid #cbd5e1", borderRadius: 6, marginBottom: 16 }}
            />

            <label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Company logo</label>
            <input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp,image/gif" onChange={handleLogoFile} />
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>PNG, JPEG, SVG, WEBP or GIF · up to ~1MB.</div>
            {reportBranding.logo_data_uri && (
              <button
                type="button"
                onClick={() => setReportBranding(prev => ({ ...prev, logo_data_uri: "" }))}
                style={{ marginTop: 8, background: "none", border: "1px solid #cbd5e1", borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontSize: 13 }}
              >
                Remove logo
              </button>
            )}

            <div style={{ marginTop: 16 }}>
              <button className="btn btn-save" onClick={saveReportBranding} disabled={savingBranding}>
                {savingBranding ? "Saving…" : "Save Report Branding"}
              </button>
              {brandingMessage && (
                <span style={{ marginLeft: 12, color: brandingMessage.includes("saved") ? "#16a34a" : "#dc2626", fontSize: 13 }}>
                  {brandingMessage}
                </span>
              )}
            </div>
          </div>

          {/* Live preview of the report header + footer with the current branding */}
          <div style={{ flex: "1 1 360px", minWidth: 320 }}>
            <label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Preview</label>
            <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden", background: "#fff" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderBottom: "1.5px solid #213a22" }}>
                {reportBranding.logo_data_uri
                  ? <img src={reportBranding.logo_data_uri} alt="logo" style={{ maxHeight: 30, maxWidth: 170, objectFit: "contain" }} />
                  : <span style={{ color: "#94a3b8", fontSize: 12, fontStyle: "italic" }}>Your logo</span>}
                <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", textAlign: "right" }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", lineHeight: 1.15 }}>
                    {reportBranding.company_name || "Your Company"}
                  </span>
                  <span style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px", color: "#213a22", marginTop: 2 }}>
                    Analysis Interpretation Report
                  </span>
                </span>
              </div>
              <div style={{ padding: "18px 12px", color: "#64748b", fontSize: 13, minHeight: 60 }}>
                Interpretation content appears here…
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 12px", borderTop: "1px solid #e2e8f0", fontSize: 11, color: "#64748b" }}>
                <span>Generated with CloudGenomics</span>
                <span>Page 1 of 3</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="org-card" style={{ display: activeSection === "credits" ? "block" : "none" }}>
        <h2>Organization Credits</h2>

        {loadingCredits ? (
          <p>Loading credits...</p>
        ) : (
          <p>
            Current Balance:{" "}
            {orgCredits === null ? "—" : orgCredits.toFixed(2)}
          </p>
        )}

        <div style={{ marginTop: "12px", display: "flex", gap: "12px" }}>
          <button className="btn btn-save" onClick={() => setShowBuyModal(true)}>
            Buy Credits
          </button>

          <button className="btn btn-edit" onClick={handleAllocateCredits}>
            Allocate Credits
          </button>

          <button
            className="btn btn-edit"
            onClick={() => {
              setShowHistoryModal(true);
              setCurrentPage(1);      // optional: reset to first page
              loadCreditHistory();
            }}
          >
            View Purchase History
          </button>

        </div>
      </div>

      {/* MONTHLY STATEMENTS & RECEIPTS */}
      <div className="org-card" style={{ display: activeSection === "statements" ? "block" : "none" }}>
        <h2>Statements &amp; Receipts</h2>
        <p style={{ color: "#64748b", marginTop: 0 }}>
          Monthly usage statements are emailed to your billing contact and available here as branded PDFs, along with receipts for each credit purchase.
        </p>
        {billingInvoices.length ? (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: "8px 10px", borderBottom: "2px solid #e2e8f0", color: "#64748b" }}>Document</th>
                <th style={{ textAlign: "left", padding: "8px 10px", borderBottom: "2px solid #e2e8f0", color: "#64748b" }}>Type</th>
                <th style={{ textAlign: "left", padding: "8px 10px", borderBottom: "2px solid #e2e8f0", color: "#64748b" }}>Period</th>
                <th style={{ textAlign: "right", padding: "8px 10px", borderBottom: "2px solid #e2e8f0", color: "#64748b" }}>Amount</th>
                <th style={{ padding: "8px 10px", borderBottom: "2px solid #e2e8f0" }}></th>
              </tr>
            </thead>
            <tbody>
              {billingInvoices.map((inv) => (
                <tr key={inv.id}>
                  <td style={{ padding: "8px 10px", borderBottom: "1px solid #eef2f7", fontWeight: 600 }}>{inv.invoice_number}</td>
                  <td style={{ padding: "8px 10px", borderBottom: "1px solid #eef2f7" }}>{inv.kind === "org_topup_receipt" ? "Receipt" : "Statement"}</td>
                  <td style={{ padding: "8px 10px", borderBottom: "1px solid #eef2f7" }}>{inv.period_start ? String(inv.period_start).slice(0, 7) : new Date(inv.created_at).toLocaleDateString()}</td>
                  <td style={{ padding: "8px 10px", borderBottom: "1px solid #eef2f7", textAlign: "right" }}>{Number(inv.total).toFixed(2)}</td>
                  <td style={{ padding: "8px 10px", borderBottom: "1px solid #eef2f7", textAlign: "right" }}>
                    <button className="btn btn-edit" onClick={() => downloadBillingInvoice(inv.id)}>Download PDF</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p style={{ color: "#64748b" }}>No statements or receipts yet.</p>
        )}
      </div>

      {/* COMPANY-WIDE CHARGES */}
      <div className="org-card" style={{ display: activeSection === "charges" ? "block" : "none" }}>
        <h2>Company Charges</h2>
        <p style={{ color: "#64748b", marginTop: 0 }}>
          All usage charges incurred across your organization. Filter by date range and user.
        </p>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-end", marginBottom: 16 }}>
          <div>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 4 }}>From</label>
            <input
              type="date"
              value={chargeStartDate}
              onChange={(e) => setChargeStartDate(e.target.value)}
              style={{ padding: "6px 8px", border: "1px solid #cbd5e1", borderRadius: 6 }}
            />
          </div>
          <div>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 4 }}>To</label>
            <input
              type="date"
              value={chargeEndDate}
              onChange={(e) => setChargeEndDate(e.target.value)}
              style={{ padding: "6px 8px", border: "1px solid #cbd5e1", borderRadius: 6 }}
            />
          </div>
          <div>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 4 }}>User</label>
            <select
              value={chargeUserFilter}
              onChange={(e) => setChargeUserFilter(e.target.value)}
              style={{ padding: "6px 8px", border: "1px solid #cbd5e1", borderRadius: 6, minWidth: 220 }}
            >
              <option value="all">All users</option>
              {chargeUsers.map((u) => (
                <option key={u.user_sub} value={u.user_sub}>
                  {u.name ? `${u.name} (${u.email || u.user_sub})` : (u.email || u.user_sub)}
                </option>
              ))}
            </select>
          </div>
          <button className="btn btn-save" onClick={loadCharges} disabled={loadingCharges}>
            {loadingCharges ? "Loading…" : "Apply"}
          </button>
          <button
            className="btn btn-edit"
            onClick={() => {
              setChargeStartDate("");
              setChargeEndDate("");
              setChargeUserFilter("all");
              // Reload with cleared filters on next tick.
              setTimeout(loadCharges, 0);
            }}
          >
            Reset
          </button>
          <button className="btn btn-edit" onClick={downloadChargesCSV} disabled={!charges.length}>
            Export CSV
          </button>
        </div>

        {/* Summary: total + per-type breakdown for the current filter */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
          <div style={{ background: "#213a22", color: "#fff", borderRadius: 8, padding: "10px 16px", minWidth: 140 }}>
            <div style={{ fontSize: 12, opacity: 0.85 }}>Total charges</div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>{Number(chargeTotals.total || 0).toFixed(2)}</div>
          </div>
          {Object.entries(chargeTotals.byType || {})
            .sort((a, b) => b[1] - a[1])
            .map(([type, amt]) => (
              <div key={type} style={{ background: "#f1f5f9", borderRadius: 8, padding: "10px 16px", minWidth: 120 }}>
                <div style={{ fontSize: 12, color: "#64748b" }}>{CHARGE_TYPE_LABELS[type] || type}</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#0f172a" }}>{Number(amt).toFixed(2)}</div>
              </div>
            ))}
        </div>

        {loadingCharges ? (
          <p>Loading charges…</p>
        ) : charges.length === 0 ? (
          <p style={{ color: "#64748b" }}>No charges found for the selected filters.</p>
        ) : (
          <div style={{ maxHeight: 480, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 8 }}>
            <table className="admin-table" style={{ width: "100%" }}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>User</th>
                  <th>Type</th>
                  <th style={{ textAlign: "right" }}>Amount</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {charges.map((c) => (
                  <tr key={c.id}>
                    <td style={{ whiteSpace: "nowrap" }}>{new Date(c.created_at).toLocaleString()}</td>
                    <td>{c.name || c.email || c.user_sub}</td>
                    <td>{CHARGE_TYPE_LABELS[c.type] || c.type}</td>
                    <td style={{ textAlign: "right", fontWeight: 600 }}>{Math.abs(Number(c.amount)).toFixed(2)}</td>
                    <td style={{ color: "#475569" }}>{c.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>


      {error && <p className="error-message">{error}</p>}

      {/* INVITE USER */}
      <div className="org-card" style={{ display: activeSection === "invite" ? "block" : "none" }}>
        <h2>Invite User</h2>
        <div style={{ display: "flex", gap: "12px", marginTop: "12px" }}>
          <input
            type="email"
            placeholder="Enter user email"
            value={inviteEmail}
            onChange={e => setInviteEmail(e.target.value)}
            style={{ flex: 1, padding: "10px", borderRadius: "8px", border: "1px solid #ccc" }}
          />
          <button className="btn btn-save" onClick={inviteUser}>
            Send Invite
          </button>
        </div>
        {inviteMessage && (
          <p style={{ marginTop: "8px", color: "#16a34a" }}>{inviteMessage}</p>
        )}
      </div>

      {/* PENDING REQUESTS */}
      <div className="org-card" style={{ display: activeSection === "pending" ? "block" : "none" }}>
        <h2>Pending Access Requests</h2>
        {pendingRequests.length === 0 ? (
          <p>No pending requests.</p>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Requested On</th>
                <th>Status</th>
                <th style={{ textAlign: "center" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pendingRequests.map(req => (
                <tr key={req.id}>
                  <td>{req.invitee_email}</td>
                  <td>{req.created_at}</td>
                  <td>
                    <span className={`badge badge-${req.status}`}>
                      {req.status}
                    </span>
                  </td>
                  <td style={{ textAlign: "center" }}>
                    {req.status === "pending" && (
                      <>
                        <button className="btn btn-edit" onClick={() => resendInvite(req.id)}>
                          Resend
                        </button>
                        <button className="btn btn-disable" onClick={() => deleteInvite(req.id)} style={{ marginLeft: "8px" }}>
                          Remove
                        </button>
                      </>
                    )}

                    {req.status === "registered" && (
                      <>
                        <button className="btn btn-save" onClick={() => approveRequest(req.invitee_email)}>
                          Add User to Org
                        </button>
                        <button className="btn btn-disable" onClick={() => deleteInvite(req.id)} style={{ marginLeft: "8px" }}>
                          Cancel Request
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* SELF-SERVICE ACCESS REQUESTS (public /request-access form) */}
      <div className="org-card" style={{ display: activeSection === "access" ? "block" : "none" }}>
        <h2>Access Requests</h2>
        <p style={{ fontSize: 13, color: "#64748b", marginTop: "-6px" }}>
          People who asked to join your organization through the public access form. Approving sends them a registration invite.
        </p>
        {accessRequests.length === 0 ? (
          <p>No access requests.</p>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Requested On</th>
                <th style={{ textAlign: "center" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {accessRequests.map(r => (
                <tr key={r.id}>
                  <td>{[r.first_name, r.last_name].filter(Boolean).join(" ")}</td>
                  <td>{r.email}</td>
                  <td>{r.phone}</td>
                  <td>{r.created_at}</td>
                  <td style={{ textAlign: "center" }}>
                    <button className="btn btn-save" onClick={() => approveAccessRequest(r.id)}>Approve</button>
                    <button className="btn btn-disable" onClick={() => rejectAccessRequest(r.id)} style={{ marginLeft: "8px" }}>Reject</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ============================
          USER CREDIT REQUESTS
      ============================ */}
      <div className="org-card" style={{ display: activeSection === "credit-requests" ? "block" : "none" }}>
        <h2>User Credit Requests</h2>

        {loadingRequests ? (
          <p>Loading requests…</p>
        ) : creditRequests.length === 0 ? (
          <p>No credit requests found.</p>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Requested Amount</th>
                <th>Status</th>
                <th>Requested On</th>
                <th>Decision</th>
              </tr>
            </thead>

            <tbody>
              {creditRequests.map(req => (
                <tr key={req.id}>
                  <td>{req.user_name || "Unknown"}</td>
                  <td>{req.user_email || req.user_sub}</td>
                  <td>{req.requested_amount}</td>
                  <td>
                    <span className={`badge badge-${req.status}`}>
                      {req.status}
                    </span>
                  </td>
                  <td>{new Date(req.created_at).toLocaleString()}</td>

                  <td>
                    {req.status === "pending" ? (
                      <>
                        <button
                          className="btn btn-save"
                          onClick={() => handleApproveRequest(req)}
                        >
                          Approve
                        </button>

                        <button
                          className="btn btn-disable"
                          style={{ marginLeft: "8px" }}
                          onClick={() => handleRejectRequest(req)}
                        >
                          Reject
                        </button>
                      </>
                    ) : (
                      req.status === "fulfilled" ? (
                        <span style={{ color: "green" }}>Approved</span>
                      ) : (
                        <span style={{ color: "red" }}>
                          Rejected{req.decision_reason ? `: ${req.decision_reason}` : ""}
                        </span>
                      )
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ============================
          ACTIVE USERS (ENABLED)
      ============================ */}
      <div className="org-card" style={{ display: activeSection === "active-users" ? "block" : "none" }}>
      <h2>Active Users</h2>

      <table className="admin-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Groups</th>
            <th>Status</th>
            <th>Credits</th>
            <th style={{ textAlign: "center" }}>Actions</th>
          </tr>
        </thead>

        <tbody>
          {users
            .filter(user => normalizedStatus(user.status) === "enabled")
            .map(user => {
              const isCGAdminUser =
                user.groups.includes("cloudgenomics") ||
                user.groups.includes("cloudgenomics-admin");

              return (
                <tr key={user.email}>
                  <td>{user.name}</td>
                  <td>{user.email}</td>

                  <td>
                    {editUser === user.email ? (
                      availableGroups.map(group => (
                        <label key={group} className="group-checkbox">
                          <input
                            type="checkbox"
                            checked={user.groups.includes(group)}
                            onChange={e =>
                              handleGroupChange(user.email, group, e.target.checked)
                            }
                            disabled={isCGAdminUser && !isSuperAdmin}
                          />
                          {group}
                        </label>
                      ))
                    ) : (
                      user.groups.join(", ")
                    )}
                  </td>

                  <td>{normalizedStatus(user.status)}</td>
                  <td>
                    {userBalances[user.user_sub] !== undefined
                      ? userBalances[user.user_sub]
                      : "—"}
                  </td>
                  <td style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                    {editUser === user.email ? (
                      <>
                        <button
                          className="btn btn-save btn-icon"
                          title="Save"
                          onClick={() => saveUserChanges(user)}
                          disabled={isCGAdminUser && !isSuperAdmin}
                        >
                          💾
                        </button>
                        <button
                          className="btn btn-cancel btn-icon"
                          title="Cancel"
                          onClick={() => setEditUser(null)}
                        >
                          ✖️
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          className="btn btn-edit btn-icon"
                          title="Edit"
                          onClick={() => setEditUser(user.email)}
                          disabled={isCGAdminUser && !isSuperAdmin}
                        >
                          ✏️
                        </button>

                        <button
                          className="btn btn-disable btn-icon"
                          title="Disable user"
                          onClick={() =>
                            toggleUserStatus(user.email, false /* isDisabled */)
                          }
                          disabled={isCGAdminUser && !isSuperAdmin}
                        >
                          🚫
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
        </tbody>
      </table>
      <p style={{ fontSize: 13, color: "#64748b", marginTop: "8px" }}>
        To permanently remove a user, first <strong>Disable</strong> them here (🚫); they then
        move to <strong>Inactive Users</strong> below, where a <strong>Remove</strong> option
        becomes available. This two-step flow prevents accidental deletions.
      </p>
      </div>


      {/* ============================
          INACTIVE USERS (DISABLED)
      ============================ */}
      <div className="org-card" style={{ display: activeSection === "inactive-users" ? "block" : "none" }}>
      <h2>Inactive Users</h2>
      <p style={{ fontSize: 13, color: "#64748b", marginTop: "-6px" }}>
        Disabled users can be re-enabled, or <strong>permanently removed</strong>. Removal deletes
        their login (Cognito) and takes them off your organization; their builds, analyses and
        files are retained under the organization. This cannot be undone.
      </p>

      <table className="admin-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Groups</th>
            <th>Credits</th>
            <th>Status</th>
            <th style={{ textAlign: "center" }}>Actions</th>
          </tr>
        </thead>

        <tbody>
          {users
            .filter(user => normalizedStatus(user.status) === "disabled")
            .map(user => {
              const isCGAdminUser =
                user.groups.includes("cloudgenomics") ||
                user.groups.includes("cloudgenomics-admin");

              return (
                <tr key={user.email}>
                  <td>{user.name}</td>
                  <td>{user.email}</td>

                  <td>
                    {editUser === user.email ? (
                      availableGroups.map(group => (
                        <label key={group} className="group-checkbox">
                          <input
                            type="checkbox"
                            checked={user.groups.includes(group)}
                            onChange={e =>
                              handleGroupChange(user.email, group, e.target.checked)
                            }
                            disabled={isCGAdminUser && !isSuperAdmin}
                          />
                          {group}
                        </label>
                      ))
                    ) : (
                      user.groups.join(", ")
                    )}
                  </td>

                  <td>{normalizedStatus(user.status)}</td>
                  <td>
                    {userBalances[user.user_sub] !== undefined
                      ? userBalances[user.user_sub]
                      : "—"}
                  </td>
                  <td style={{ textAlign: "center" }}>
                    {editUser === user.email ? (
                      <>
                        <button
                          className="btn btn-save"
                          onClick={() => saveUserChanges(user)}
                          disabled={isCGAdminUser && !isSuperAdmin}
                        >
                          Save
                        </button>
                        <button
                          className="btn btn-cancel"
                          onClick={() => setEditUser(null)}
                          style={{ marginLeft: "8px" }}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          className="btn btn-edit"
                          onClick={() => setEditUser(user.email)}
                          disabled={isCGAdminUser && !isSuperAdmin}
                        >
                          Edit
                        </button>

                        <button
                          className="btn btn-enable"
                          onClick={() =>
                            toggleUserStatus(user.email, true /* isDisabled */)
                          }
                          disabled={isCGAdminUser && !isSuperAdmin}
                          style={{ marginLeft: "8px" }}
                        >
                          Enable
                        </button>

                        <button
                          className="btn btn-disable"
                          onClick={() => removeUser(user.email)}
                          disabled={isCGAdminUser && !isSuperAdmin}
                          style={{ marginLeft: "8px" }}
                        >
                          Remove
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
        </tbody>
      </table>
      </div>

        </div>{/* end admin-content */}
      </div>{/* end admin-layout */}

      {showResourceModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Request Resource Change</h2>

            <label>Max vCPU</label>
            <input
              type="number"
              value={resourceForm.max_vcpu}
              onChange={e => setResourceForm({ ...resourceForm, max_vcpu: e.target.value })}
            />

            <label>Max Memory (GB)</label>
            <input
              type="number"
              value={resourceForm.max_memory}
              onChange={e => setResourceForm({ ...resourceForm, max_memory: e.target.value })}
            />

            <label>Max Concurrent Users</label>
            <input
              type="number"
              value={resourceForm.max_concurrent_users}
              onChange={e =>
                setResourceForm({ ...resourceForm, max_concurrent_users: e.target.value })
              }
            />

            <div className="modal-actions">
              <button
                className="btn btn-save"
                onClick={() => setShowConfirm(true)}
              >
                Save
              </button>

              <button
                className="btn btn-cancel"
                onClick={() => setShowResourceModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showConfirm && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Confirm Resource Change</h3>
            <p>Are you sure you want to submit this request?</p>

            <ul>
              <li><strong>Max vCPU:</strong> {resourceForm.max_vcpu}</li>
              <li><strong>Max Memory:</strong> {resourceForm.max_memory} GB</li>
              <li><strong>Max Concurrent Users:</strong> {resourceForm.max_concurrent_users}</li>
            </ul>

            <div className="modal-actions">
              <button className="btn btn-save" onClick={submitResourceChange}>
                Confirm
              </button>
              <button className="btn btn-cancel" onClick={() => setShowConfirm(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showBuyModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Buy Credits</h2>

            <label>Enter number of credits you'd like to purchase (minimum $100).<br/>$1 = 1 credit</label>
            <input
              type="number"
              value={buyAmount}
              onChange={(e) => setBuyAmount(e.target.value)}
              min="100"
              style={{ padding: "10px", borderRadius: "8px", border: "1px solid #ccc" }}
            />

            <div className="modal-actions" style={{ marginTop: "20px" }}>
              <button
                className="btn btn-save"
                onClick={() => {
                  setShowBuyModal(false);
                  handleBuyCredits();
                }}
              >
                Proceed to Checkout
              </button>

              <button
                className="btn btn-cancel"
                onClick={() => setShowBuyModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showHistoryModal && (
        <div className="modal-overlay">
          <div
            className="modal"
            style={{
              width: "90%",
              maxWidth: "1200px",
              maxHeight: "80vh",
              overflow: "hidden"
            }}
          >
            <h2>Purchase History</h2>
            
            {/* FILTER BAR */}
            <div
              style={{
                display: "flex",
                gap: "12px",
                marginTop: "12px",
                flexWrap: "wrap"
              }}
            >
              {/* Start Date */}
              <div>
                <label style={{ fontSize: "12px" }}>Start Date</label>
                <input
                  type="date"
                  value={filterStartDate}
                  onChange={(e) => setFilterStartDate(e.target.value)}
                  style={{ padding: "6px", borderRadius: "6px", border: "1px solid #ccc" }}
                />
              </div>

              {/* End Date */}
              <div>
                <label style={{ fontSize: "12px" }}>End Date</label>
                <input
                  type="date"
                  value={filterEndDate}
                  onChange={(e) => setFilterEndDate(e.target.value)}
                  style={{ padding: "6px", borderRadius: "6px", border: "1px solid #ccc" }}
                />
              </div>

              {/* Type Filter */}
              <div>
                <label style={{ fontSize: "12px" }}>Type</label>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  style={{ padding: "6px", borderRadius: "6px", border: "1px solid #ccc" }}
                >
                  <option value="all">All</option>
                  <option value="purchase">Purchase</option>
                  <option value="allocation">Allocation</option>
                  <option value="refund">Refund</option>
                </select>
              </div>

              {/* Min Amount */}
              <div>
                <label style={{ fontSize: "12px" }}>Min Amount</label>
                <input
                  type="number"
                  value={filterMinAmount}
                  onChange={(e) => setFilterMinAmount(e.target.value)}
                  style={{ paddingLeft: "6px", borderRadius: "6px", border: "1px solid #ccc" }}
                />
              </div>

              {/* Max Amount */}
              <div>
                <label style={{ fontSize: "12px" }}>Max Amount</label>
                <input
                  type="number"
                  value={filterMaxAmount}
                  onChange={(e) => setFilterMaxAmount(e.target.value)}
                  style={{ padding: "6px", borderRadius: "6px", border: "1px solid #ccc" }}
                />
              </div>
            </div>

            <div style={{ display: "flex", gap: "12px", marginTop: "8px" }}>
              <button
                className="btn btn-save"
                onClick={() => {
                  setCurrentPage(1);
                  // nothing else needed — table uses filtered data automatically
                }}
              >
                Apply Filters
              </button>

              <button
                className="btn btn-cancel"
                onClick={() => {
                  setFilterStartDate("");
                  setFilterEndDate("");
                  setFilterType("all");
                  setFilterMinAmount("");
                  setFilterMaxAmount("");
                  setCurrentPage(1);
                }}
              >
                Clear Filters
              </button>
            </div>

            {/* Search Box */}
            <div style={{ flex: "1 1 200px" }}>
              <label style={{ fontSize: "12px", }}>Search</label>
              <input
                type="text"
                placeholder="Search description, type, amount..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ padding: "6px", borderRadius: "6px", border: "1px solid #ccc", width: "15%" }}
              />
            </div>

            <button
              className="btn btn-save"
              style={{ float: "right", marginTop: "-40px" }}
              onClick={downloadCSV}
            >
              Download CSV
            </button>

            {/* Scrollable table container */}
            <div style={{
              maxHeight: "350px",
              overflowY: "auto",
              marginTop: "16px",
              border: "1px solid #ddd",
              borderRadius: "8px"
            }}>
              {loadingHistory ? (
                <p style={{ padding: "12px" }}>Loading history…</p>
              ) : creditHistory.length === 0 ? (
                <p style={{ padding: "12px" }}>No credit purchases found.</p>
              ) : (
                <table className="admin-table" style={{ margin: 0 }}>
                  <thead>
                    <tr>
                      <th onClick={() => handleSort("created_at")} style={{ cursor: "pointer" }}>
                        Date {sortColumn === "created_at" ? (sortDirection === "asc" ? "▲" : "▼") : ""}
                      </th>

                      <th onClick={() => handleSort("type")} style={{ cursor: "pointer" }}>
                        Type {sortColumn === "type" ? (sortDirection === "asc" ? "▲" : "▼") : ""}
                      </th>

                      <th onClick={() => handleSort("amount")} style={{ cursor: "pointer" }}>
                        Amount {sortColumn === "amount" ? (sortDirection === "asc" ? "▲" : "▼") : ""}
                      </th>

                      <th onClick={() => handleSort("description")} style={{ cursor: "pointer" }}>
                        Description {sortColumn === "description" ? (sortDirection === "asc" ? "▲" : "▼") : ""}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted
                      .slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
                      .map((entry, idx) => (
                        <tr key={idx}>
                          <td>{new Date(entry.created_at).toLocaleString()}</td>
                          <td>{entry.type}</td>
                          <td>${Number(entry.amount).toFixed(2)}</td>
                          <td>{entry.description}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination Controls */}
            {creditHistory.length > rowsPerPage && (
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: "16px"
              }}>
                <button
                  className="btn btn-edit"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  Previous
                </button>

                <span>
                  Page {currentPage} of {Math.ceil(creditHistory.length / rowsPerPage)}
                </span>

                <button
                  className="btn btn-edit"
                  disabled={currentPage === Math.ceil(creditHistory.length / rowsPerPage)}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  Next
                </button>
              </div>
            )}

            {/* Modal Actions */}
            <div className="modal-actions" style={{ marginTop: "20px" }}>
              <button
                className="btn btn-cancel"
                onClick={() => setShowHistoryModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showAllocateModal && (
        <div className="modal-overlay">
          <div
            className="modal"
            style={{
              width: "90%",
              maxWidth: "1200px",
              maxHeight: "80vh",
              overflow: "hidden"
            }}
          >
            <h2>Allocate Credits</h2>

            {loadingUserBalances ? (
              <p>Loading user balances…</p>
            ) : (
              <>
                <h4>PLEASE NOTE: CREDITS ONCE ALLOCATED TO USERS CANNOT BE RETRACTED, REFUNDED OR TRANSFERRED.<br/>MAKE SURE TO ALLOCATE CAREFULLY AND ONLY TO USERS YOU TRUST.</h4>
                <div
                  style={{
                    maxHeight: "60vh",
                    overflowY: "auto",
                    marginTop: "16px",
                    border: "1px solid #ddd",
                    borderRadius: "8px"
                  }}
                >
                  {allocationError && (
                    <p style={{ color: "red", marginTop: "8px" }}>
                      {allocationError}
                    </p>
                  )}
                  
                  <table className="admin-table" style={{ margin: 0 }}>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Current Balance</th>
                        <th>Allocate</th>
                      </tr>
                    </thead>

                    <tbody>
                      {users
                        .filter(u => normalizedStatus(u.status) === "enabled")
                        .map(u => (
                          <tr key={u.user_sub}>
                            <td>{u.name}</td>
                            <td>{u.email}</td>
                            <td>
                              {userBalances[u.user_sub] !== undefined
                                ? userBalances[u.user_sub]
                                : "—"}
                            </td>
                            <td>
                              <input
                                type="number"
                                min="0"
                                step="1"
                                value={userAllocations[u.user_sub] ?? 0}
                                onChange={(e) => {
                                  const val = Number(e.target.value);
                                  setUserAllocations(prev => ({
                                    ...prev,
                                    [u.user_sub]: isNaN(val) ? 0 : val
                                  }));
                                  validateAllocations();
                                }}
                                style={{
                                  width: "80px",
                                  padding: "6px",
                                  borderRadius: "6px",
                                  border: "1px solid #ccc"
                                }}
                              />
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            <div className="modal-actions" style={{ marginTop: "20px" }}>
              <button
                className="btn btn-save"
                disabled={allocationError !== ""}
                onClick={handleSubmitAllocation}
              >
                Save Allocation
              </button>


              <button
                className="btn btn-cancel"
                onClick={() => setShowAllocateModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}


    </RequirePermission>
    </div>
  );
};

export default OrgAdminPage;